// Largest-Triangle-Three-Buckets (LTTB) downsampling. Keeps the visual shape of
// a dense series while cutting the point count Recharts has to render — the key
// to smooth charts when a real-time/large dataset arrives (thousands of ticks).
//
// For the current mock data (<=96 points) this is a no-op; it exists so dense
// production series degrade gracefully instead of janking the SVG renderer.

export function downsampleLTTB<T>(
  data: readonly T[],
  threshold: number,
  getY: (point: T) => number,
): T[] {
  const n = data.length;
  if (threshold >= n || threshold <= 2) return data.slice();

  const sampled: T[] = [];
  const bucketSize = (n - 2) / (threshold - 2);

  let a = 0; // initially the first point is the anchor
  sampled.push(data[0]!);

  for (let i = 0; i < threshold - 2; i++) {
    // Average point of the *next* bucket (used as the triangle's far vertex).
    let avgX = 0;
    let avgY = 0;
    const nextStart = Math.floor((i + 1) * bucketSize) + 1;
    let nextEnd = Math.floor((i + 2) * bucketSize) + 1;
    nextEnd = nextEnd < n ? nextEnd : n;
    const nextLen = nextEnd - nextStart;
    for (let j = nextStart; j < nextEnd; j++) {
      avgX += j;
      avgY += getY(data[j]!);
    }
    avgX /= nextLen;
    avgY /= nextLen;

    // Pick the point in the current bucket that forms the largest triangle.
    const rangeStart = Math.floor(i * bucketSize) + 1;
    const rangeEnd = Math.floor((i + 1) * bucketSize) + 1;
    const anchorX = a;
    const anchorY = getY(data[a]!);

    let maxArea = -1;
    let chosen = rangeStart;
    for (let j = rangeStart; j < rangeEnd; j++) {
      const area =
        Math.abs(
          (anchorX - avgX) * (getY(data[j]!) - anchorY) -
            (anchorX - j) * (avgY - anchorY),
        ) * 0.5;
      if (area > maxArea) {
        maxArea = area;
        chosen = j;
      }
    }
    sampled.push(data[chosen]!);
    a = chosen;
  }

  sampled.push(data[n - 1]!);
  return sampled;
}
